import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Stack } from '@fluentui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen, faChevronDown, faExternalLink, faShip } from '@fortawesome/pro-light-svg-icons';
import { IOperationItem } from '../types';
import Text from '../../../typography/text';
import OrderService from '../../../../services/orders/order';

interface IOrderBadgeProps {
    item: IOperationItem;
    size?: 'compact' | 'normal';
}

export const OrderBadge: React.FC<IOrderBadgeProps> = ({ item, size = 'normal' }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const popoverRef = useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const orders = item.orders || [];
    const hasMultipleOrders = orders.length > 1;
    const firstOrder = orders.length > 0 ? orders[0] : undefined;

    // Case 1: Multiple / General (No order attached yet)
    if (!item.orderNumber && orders.length === 0) {
        return (
            <Stack tokens={{ childrenGap: 2 }}>
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: '#f8fafc',
                        border: '1px dashed #cbd5e1',
                        borderRadius: '6px',
                        padding: size === 'compact' ? '1px 6px' : '2px 8px',
                        color: '#64748b',
                        fontSize: size === 'compact' ? '11px' : '12px',
                        fontWeight: 500,
                        width: 'fit-content'
                    }}
                    title="Jadwal kapal ini belum ditautkan ke order muatan tertentu di sistem"
                >
                    <FontAwesomeIcon icon={faBoxOpen} style={{ fontSize: '11px', color: '#94a3b8' }} />
                    Multiple / Belum Ada Muatan
                </span>
                {item.itemType === 'ship' && (
                    <NavLink
                        to={item.detailsLink}
                        style={{
                            fontSize: '11px',
                            color: '#0284c7',
                            textDecoration: 'none',
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px'
                        }}
                    >
                        <span>Lihat Jadwal Kapal</span>
                        <FontAwesomeIcon icon={faExternalLink} style={{ fontSize: '9px' }} />
                    </NavLink>
                )}
            </Stack>
        );
    }

    // Single order (no multiple popover needed)
    if (!hasMultipleOrders) {
        const displayLabel = item.orderNumber?.replace(/\s*\(\+\d+\)/, '') || 
            (firstOrder ? `#${OrderService.getOrderNumber(firstOrder.orderNumber)}` : 'Order');

        return (
            <Stack tokens={{ childrenGap: 2 }}>
                <NavLink 
                    to={item.orderLink || (firstOrder ? `/orders/${firstOrder.id}` : '#')} 
                    style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                >
                    <Text size={size === 'compact' ? 'xsmall' : 'small'} style={{ color: '#0284c7', fontWeight: 600 }}>
                        {displayLabel}
                    </Text>
                </NavLink>
                {item.customerName && (
                    <Text size="xsmall" style={{ color: '#64748b' }}>
                        {item.customerName}
                    </Text>
                )}
            </Stack>
        );
    }

    // Case 2: Multi-Order (orders.length > 1) with Interactive Dropdown/Popover
    const primaryLabel = firstOrder ? `#${OrderService.getOrderNumber(firstOrder.orderNumber)}` : (item.orderNumber?.split(' ')[0] || 'Order');
    const extraCount = orders.length - 1;

    return (
        <div style={{ position: 'relative', display: 'inline-block' }} ref={popoverRef}>
            <Stack tokens={{ childrenGap: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    {/* Primary Order link */}
                    <NavLink 
                        to={item.orderLink || (firstOrder ? `/orders/${firstOrder.id}` : '#')} 
                        style={{ textDecoration: 'none' }}
                    >
                        <Text size={size === 'compact' ? 'xsmall' : 'small'} style={{ color: '#0284c7', fontWeight: 600 }}>
                            {primaryLabel}
                        </Text>
                    </NavLink>

                    {/* Interactive Badge for +N */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsOpen(!isOpen);
                        }}
                        style={{
                            border: '1px solid #bae6fd',
                            background: isOpen ? '#0284c7' : '#e0f2fe',
                            color: isOpen ? '#ffffff' : '#0369a1',
                            borderRadius: '12px',
                            padding: '1px 8px',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.15s ease',
                            outline: 'none'
                        }}
                        title="Klik untuk melihat daftar lengkap order yang ada di kapal ini"
                    >
                        <span>+{extraCount} Order</span>
                        <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '9px', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }} />
                    </button>
                </div>

                {item.customerName && (
                    <Text size="xsmall" style={{ color: '#64748b' }}>
                        {item.customerName}
                    </Text>
                )}
            </Stack>

            {/* Popover Dropdown */}
            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        zIndex: 9999,
                        minWidth: '240px',
                        maxWidth: '300px',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                        padding: '10px 12px',
                        fontSize: '12px',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '6px', borderBottom: '1px solid #f1f5f9', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 600, color: '#334155', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Daftar Order ({orders.length})
                        </span>
                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                            {item.shipName ? item.shipName : ''}
                        </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '180px', overflowY: 'auto' }}>
                        {orders.map((ord, idx) => (
                            <NavLink
                                key={ord.id || idx}
                                to={`/orders/${ord.id}`}
                                style={{
                                    textDecoration: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '5px 8px',
                                    borderRadius: '6px',
                                    background: idx === 0 ? '#f8fafc' : 'transparent',
                                    border: '1px solid transparent',
                                    color: '#0f172a',
                                    transition: 'background 0.15s ease',
                                }}
                                onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLElement).style.background = '#f1f5f9';
                                }}
                                onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLElement).style.background = idx === 0 ? '#f8fafc' : 'transparent';
                                }}
                            >
                                <span style={{ fontWeight: 600, color: '#0284c7' }}>
                                    #{OrderService.getOrderNumber(ord.orderNumber)}
                                </span>
                                {idx === 0 && (
                                    <span style={{ fontSize: '10px', color: '#64748b', background: '#e2e8f0', padding: '1px 5px', borderRadius: '4px' }}>
                                        Utama
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </div>

                    {item.detailsLink && (
                        <div style={{ borderTop: '1px solid #f1f5f9', marginTop: '8px', paddingTop: '6px', textAlign: 'right' }}>
                            <NavLink
                                to={item.detailsLink}
                                style={{
                                    fontSize: '11px',
                                    color: '#0284c7',
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                <FontAwesomeIcon icon={faShip} style={{ fontSize: '10px' }} />
                                <span>Lihat Jadwal Lengkap</span>
                            </NavLink>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
